// src/components/DynamicDesignGenerator.jsx
// Dynamic design generator using AI analysis + 600+ backgrounds + CESDK

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, Flex, Heading, Text, Button, SimpleGrid, VStack, HStack, 
  Badge, Spinner, Progress, Alert, AlertIcon, Image, Tabs, TabList, 
  TabPanels, Tab, TabPanel, useColorModeValue, Icon, Divider
} from '@chakra-ui/react';
import { useFeatureFlag, isBetaUser } from '../utils/featureFlags';
import { analyzeImageContent } from '../utils/contentAnalysis';
import { generateDynamicScene, processEventTags, LAYOUT_TEMPLATES } from '../utils/dynamicSceneGenerator';
import { selectOptimalBackgrounds, BACKGROUND_CATEGORIES } from '../utils/backgroundLibrary';

/**
 * Dynamic Design Generator - Creates AI-powered sports designs on-the-fly
 * Uses: Content Analysis + Background Library + Dynamic Scene Generation + Event Tags
 */
const DynamicDesignGenerator = ({ uploadedImageURL, eventTags = {}, user }) => {
  // State management
  const [analysisData, setAnalysisData] = useState(null);
  const [generatedDesigns, setGeneratedDesigns] = useState([]);
  const [recommendedBackgrounds, setRecommendedBackgrounds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState('analyzing');
  const [progress, setProgress] = useState(0);
  
  const navigate = useNavigate();
  
  // Feature flags
  const dynamicGenerationEnabled = useFeatureFlag('DYNAMIC_DESIGN_GENERATION');
  const isUserBeta = user?.email ? isBetaUser(user.email) : false;
  const hasAccess = dynamicGenerationEnabled || isUserBeta;

  // Process event tags for better AI context
  const processedEventTags = processEventTags(eventTags);

  // Auto-generate when component mounts
  useEffect(() => {
    if (uploadedImageURL && hasAccess) {
      generateDynamicDesigns();
    }
  }, [uploadedImageURL, hasAccess]);

  /**
   * Main generation pipeline
   */
  const generateDynamicDesigns = async () => {
    if (!uploadedImageURL) return;

    setLoading(true);
    setError(null);
    setProgress(0);
    console.log('🎨 Starting dynamic design generation pipeline');

    try {
      // Step 1: Analyze content (20% progress)
      setCurrentStep('analyzing');
      setProgress(20);
      console.log('📊 Step 1: Analyzing content...');
      
      const analysisResult = await analyzeImageContent(uploadedImageURL);
      if (!analysisResult.success) {
        throw new Error('Content analysis failed');
      }
      
      setAnalysisData(analysisResult.analysis);
      console.log('✅ Content analysis complete:', analysisResult.analysis);

      // Step 2: Select optimal backgrounds (40% progress)
      setCurrentStep('backgrounds');
      setProgress(40);
      console.log('🌄 Step 2: Selecting optimal backgrounds...');
      
      const backgrounds = await selectOptimalBackgrounds(
        analysisResult.analysis,
        processedEventTags,
        { maxResults: 5 }
      );
      
      setRecommendedBackgrounds(backgrounds);
      console.log('✅ Background selection complete:', backgrounds.length, 'backgrounds');

      // Step 3: Generate dynamic scenes (60% progress)
      setCurrentStep('generating');
      setProgress(60);
      console.log('🎭 Step 3: Generating dynamic scenes...');
      
      const designs = await generateMultipleDesigns(
        uploadedImageURL,
        analysisResult.analysis,
        processedEventTags,
        backgrounds
      );
      
      setGeneratedDesigns(designs);
      console.log('✅ Scene generation complete:', designs.length, 'designs');

      // Step 4: Finalize (100% progress)
      setCurrentStep('complete');
      setProgress(100);
      console.log('🎉 Dynamic design generation complete!');

    } catch (err) {
      console.error('❌ Dynamic design generation failed:', err);
      setError(`Generation failed: ${err.message}`);
      setCurrentStep('error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Generates multiple design variations
   */
  const generateMultipleDesigns = async (imageURL, analysis, eventTags, backgrounds) => {
    const designs = [];
    const layouts = Object.keys(LAYOUT_TEMPLATES);
    
    // Generate one design per layout template with different backgrounds
    for (let i = 0; i < Math.min(layouts.length, backgrounds.length); i++) {
      const layoutKey = layouts[i];
      const background = backgrounds[i] || backgrounds[0];
      
      const sceneResult = await generateDynamicScene({
        uploadedImageURL: imageURL,
        contentAnalysis: analysis,
        eventTags: eventTags,
        preferredLayout: layoutKey
      });
      
      if (sceneResult.success) {
        designs.push({
          id: `dynamic_${i}`,
          name: `${LAYOUT_TEMPLATES[layoutKey].name}`,
          description: LAYOUT_TEMPLATES[layoutKey].description,
          sceneConfig: sceneResult.sceneConfig,
          metadata: sceneResult.metadata,
          background: background,
          layoutType: layoutKey,
          isGenerated: true,
          smartScore: 0.9, // High score for AI-generated designs
          matchReasons: [
            `AI-generated ${LAYOUT_TEMPLATES[layoutKey].name.toLowerCase()}`,
            `Optimized for ${analysis.sport?.primary || 'sports'} content`,
            `Perfect background match (${Math.round(background.matchScore * 100)}%)`
          ]
        });
      }
    }
    
    return designs;
  };

  /**
   * Handles design selection - routes to CESDK editor with scene config
   */
  const handleDesignSelect = (design) => {
    console.log('🎯 Selected dynamic design:', design.name);
    
    // Navigate to CESDK customization with dynamic scene config
    navigate('/customize/dynamic', {
      state: {
        sceneConfig: design.sceneConfig,
        uploadedImageURL,
        metadata: design.metadata,
        eventTags: processedEventTags,
        designType: 'dynamic_generated'
      }
    });
  };

  // Loading states with progress
  if (loading) {
    return (
      <Box p={6} bg="white" borderRadius="xl" boxShadow="sm" border="1px" borderColor="gray.200">
        <VStack spacing={6} textAlign="center">
          <Box>
            <Heading size="lg" color="gray.800" mb={2}>
              🎨 Creating Your AI-Powered Designs
            </Heading>
            <Text color="gray.600">
              Analyzing your photo and generating perfect sports layouts...
            </Text>
          </Box>
          
          <Box w="100%" maxW="400px">
            <Progress value={progress} colorScheme="blue" size="lg" borderRadius="full" />
            <Text fontSize="sm" color="gray.500" mt={2}>
              {progress}% Complete
            </Text>
          </Box>
          
          <VStack spacing={2}>
            <HStack>
              <Spinner size="sm" color="blue.500" />
              <Text fontSize="sm" fontWeight="medium" color="blue.600">
                {getStepMessage(currentStep)}
              </Text>
            </HStack>
            
            {currentStep === 'analyzing' && (
              <Text fontSize="xs" color="gray.500">Detecting sport, people, and layout preferences</Text>
            )}
            {currentStep === 'backgrounds' && (
              <Text fontSize="xs" color="gray.500">Selecting from 600+ HD sport backgrounds</Text>
            )}
            {currentStep === 'generating' && (
              <Text fontSize="xs" color="gray.500">Creating custom layouts with CESDK</Text>
            )}
          </VStack>
        </VStack>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        <Box>
          <Text fontWeight="bold">Generation Failed</Text>
          <Text>{error}</Text>
          <Button mt={2} size="sm" onClick={generateDynamicDesigns}>
            Try Again
          </Button>
        </Box>
      </Alert>
    );
  }

  // Success state - show generated designs
  return (
    <Box>
      {/* Generation Summary */}
      {analysisData && (
        <Box mb={6} p={4} bg="green.50" borderRadius="xl" border="1px" borderColor="green.200">
          <Flex align="center" justify="space-between">
            <Box>
              <HStack spacing={2} mb={2}>
                <Text fontSize="20px">✨</Text>
                <Heading size="md" color="green.800">AI Generation Complete!</Heading>
              </HStack>
              <Text color="green.700" fontSize="sm">
                Created {generatedDesigns.length} custom designs for your {analysisData.sport?.primary || 'sports'} content
                {processedEventTags.playerName && ` featuring ${processedEventTags.playerName}`}
              </Text>
            </Box>
            <VStack spacing={1} align="end">
              <Badge colorScheme="green" variant="solid">AI Generated</Badge>
              <Text fontSize="xs" color="green.600">
                {Math.round((analysisData.confidence?.overall || 0.8) * 100)}% confidence
              </Text>
            </VStack>
          </Flex>
        </Box>
      )}

      {/* Design Results with Tabs */}
      <Tabs variant="enclosed" colorScheme="blue">
        <TabList>
          <Tab>
            <HStack>
              <Text>🎨</Text>
              <Text>Generated Designs ({generatedDesigns.length})</Text>
            </HStack>
          </Tab>
          <Tab>
            <HStack>
              <Text>🌄</Text>
              <Text>Background Library ({recommendedBackgrounds.length})</Text>
            </HStack>
          </Tab>
          <Tab>
            <HStack>
              <Text>🧠</Text>
              <Text>AI Analysis</Text>
            </HStack>
          </Tab>
        </TabList>

        <TabPanels>
          {/* Generated Designs Tab */}
          <TabPanel px={0}>
            <Box mb={4}>
              <Text color="gray.600" mb={4}>
                These designs were created specifically for your content using AI analysis, 
                optimal backgrounds, and dynamic layouts.
              </Text>
            </Box>

            <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={6}>
              {generatedDesigns.map((design) => (
                <Box
                  key={design.id}
                  bg="white"
                  borderRadius="xl"
                  overflow="hidden"
                  boxShadow="lg"
                  border="2px"
                  borderColor="green.300"
                  _hover={{ 
                    transform: 'translateY(-4px)', 
                    boxShadow: 'xl',
                    borderColor: 'green.400'
                  }}
                  transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                  cursor="pointer"
                  onClick={() => handleDesignSelect(design)}
                  position="relative"
                >
                  {/* AI Generated Badge */}
                  <Badge 
                    position="absolute" 
                    top={2} 
                    left={2} 
                    colorScheme="purple" 
                    variant="solid"
                    zIndex={2}
                  >
                    AI Generated
                  </Badge>
                  
                  {/* Match Score Badge */}
                  <Badge 
                    position="absolute" 
                    top={2} 
                    right={2} 
                    colorScheme="green" 
                    variant="solid"
                    zIndex={2}
                  >
                    {Math.round(design.smartScore * 100)}% Match
                  </Badge>

                  {/* Design Preview */}
                  <Box position="relative" overflow="hidden" bg="gray.50" h="300px">
                    {/* Background Preview */}
                    <Box
                      position="absolute"
                      inset="0"
                      bgImage={`url(${design.background.url})`}
                      bgSize="cover"
                      bgPosition="center"
                      opacity="0.7"
                    />
                    
                    {/* Layout Overlay */}
                    <Box
                      position="absolute"
                      inset="0"
                      bg="blackAlpha.600"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <VStack spacing={3} textAlign="center" color="white">
                        <Text fontSize="48px">🎨</Text>
                        <Heading size="md">{design.name}</Heading>
                        <Text fontSize="sm" opacity="0.9">{design.description}</Text>
                        <Button
                          colorScheme="green"
                          size="lg"
                          leftIcon={<Text>✨</Text>}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDesignSelect(design);
                          }}
                        >
                          Open in Editor
                        </Button>
                      </VStack>
                    </Box>
                  </Box>
                  
                  {/* Design Info */}
                  <Box p={4}>
                    <VStack align="start" spacing={2}>
                      <Heading size="sm" color="gray.800">{design.name}</Heading>
                      
                      <HStack spacing={2} flexWrap="wrap">
                        <Badge colorScheme="blue" size="sm">Dynamic Layout</Badge>
                        <Badge colorScheme="green" size="sm">Smart Background</Badge>
                        <Badge colorScheme="purple" size="sm">AI Optimized</Badge>
                      </HStack>
                      
                      <VStack align="start" spacing={1}>
                        {design.matchReasons.slice(0, 2).map((reason, idx) => (
                          <Text key={idx} fontSize="xs" color="gray.600">
                            • {reason}
                          </Text>
                        ))}
                      </VStack>
                    </VStack>
                  </Box>
                </Box>
              ))}
            </SimpleGrid>
          </TabPanel>

          {/* Background Library Tab */}
          <TabPanel px={0}>
            <Box mb={4}>
              <Text color="gray.600" mb={4}>
                These backgrounds were selected from your 600+ HD library based on 
                sport detection and content analysis.
              </Text>
            </Box>

            <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} spacing={4}>
              {recommendedBackgrounds.map((bg, idx) => (
                <Box
                  key={idx}
                  position="relative"
                  borderRadius="lg"
                  overflow="hidden"
                  _hover={{ transform: 'scale(1.02)' }}
                  transition="transform 0.2s"
                >
                  <Image
                    src={bg.url}
                    alt={`Background ${idx + 1}`}
                    w="100%"
                    h="120px"
                    objectFit="cover"
                  />
                  <Box
                    position="absolute"
                    bottom="0"
                    left="0"
                    right="0"
                    bg="blackAlpha.700"
                    color="white"
                    p={2}
                  >
                    <Text fontSize="xs" fontWeight="bold">
                      {Math.round(bg.matchScore * 100)}% Match
                    </Text>
                    <Text fontSize="xs" opacity="0.8">
                      {bg.matchReasons[0]}
                    </Text>
                  </Box>
                </Box>
              ))}
            </SimpleGrid>
          </TabPanel>

          {/* AI Analysis Tab */}
          <TabPanel px={0}>
            {analysisData && (
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                <Box p={4} bg="blue.50" borderRadius="lg">
                  <Heading size="sm" mb={3} color="blue.800">Content Characteristics</Heading>
                  <VStack align="start" spacing={2} fontSize="sm">
                    <Text><strong>Sport:</strong> {analysisData.sport?.primary || 'Baseball'}</Text>
                    <Text><strong>People Count:</strong> {analysisData.characteristics?.peopleCount || 1}</Text>
                    <Text><strong>Orientation:</strong> {
                      analysisData.characteristics?.isPortrait ? 'Portrait' : 
                      analysisData.characteristics?.isLandscape ? 'Landscape' : 'Square'
                    }</Text>
                    <Text><strong>Brightness:</strong> {analysisData.characteristics?.brightness || 'Medium'}</Text>
                  </VStack>
                </Box>
                
                <Box p={4} bg="green.50" borderRadius="lg">
                  <Heading size="sm" mb={3} color="green.800">Event Context</Heading>
                  <VStack align="start" spacing={2} fontSize="sm">
                    <Text><strong>Player:</strong> {processedEventTags.playerName || 'Not specified'}</Text>
                    <Text><strong>Team:</strong> {processedEventTags.teamName || 'Not specified'}</Text>
                    <Text><strong>Position:</strong> {processedEventTags.position || 'Not specified'}</Text>
                    <Text><strong>Season:</strong> {processedEventTags.season || 'Current'}</Text>
                  </VStack>
                </Box>
              </SimpleGrid>
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

/**
 * Helper function to get step messages
 */
const getStepMessage = (step) => {
  const messages = {
    analyzing: 'Analyzing your photo with AI...',
    backgrounds: 'Selecting optimal backgrounds...',
    generating: 'Creating dynamic layouts...',
    complete: 'Generation complete!',
    error: 'Something went wrong'
  };
  
  return messages[step] || 'Processing...';
};

export default DynamicDesignGenerator; 